import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DASHBOARD_ON_CHAT, SUGGESTIONS_ON_CHAT } from "@/lib/features";
import { cn } from "@/lib/utils";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMobileChatMode } from "@/hooks/useMobileChatMode";
import MobileChatHeader from "@/components/MobileChatHeader";
import {
  CAPABILITY_CATEGORIES,
  CAPABILITY_FOLLOWUPS,
  isGeneralResponse,
} from "@/lib/capabilities";

interface SuggestionItem {
  icon: string;
  text: string;
}

const DEFAULT_SUGGESTIONS: SuggestionItem[] = [
  { icon: "📊", text: "Summarize my latest data" },
  { icon: "🔍", text: "Search across my connected sources" },
  { icon: "📝", text: "Generate a report from recent activity" },
  { icon: "💡", text: "What insights can you find?" },
];

const SUGGESTION_ICONS = ["💬", "📊", "🔍", "📝", "💡", "🚀", "📈", "🎯"];

import ChatSidebar from "@/components/ChatSidebar";
import ChatMessage from "@/components/ChatMessage";
import ChatInput from "@/components/ChatInput";
import DashboardPanel, { DashboardSpec } from "@/components/DashboardPanel";
import HowItWorksDialog from "@/components/HowItWorksDialog";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import { Loader2, Menu, Wrench, LayoutDashboard, ChevronRight, Sparkles, MessageSquare, RotateCcw, AlertTriangle, LayoutTemplate } from "lucide-react";
import databullLogo from "@/assets/databulllogo.png";
import { APP_NAME } from "@/lib/brand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import DashboardSubscribeDialog from "@/components/admin/DashboardSubscribeDialog";
import ConversationDebugDialog from "@/components/ConversationDebugDialog";
import { useAuditLog } from "@/hooks/useAuditLog";
import { usePiiMasking } from "@/hooks/usePiiMasking";
import type { ConfiguredFilter, FilterSuggestionsResult } from "@/components/FilterSuggestionsDialog";
import { TemplateBrowserDialog, TemplatePromptDialog } from "@/components/TemplateBrowserDialog";
import type { WorkflowTemplate } from "@/lib/template-constants";

interface Message {
  id?: string;
  role: "user" | "assistant";
  content: string;
  usedTools?: boolean;
  createdAt?: string;
  status?: "pending" | "processing" | "complete" | "error";
}

interface DataSource {
  id: string;
  name: string;
  enabled: boolean;
}

interface DashboardSnapshot {
  id: string;
  title: string;
  spec: any;
  created_at: string;
  updated_at?: string;
  message_id?: string | null;
}

// Per-conversation streaming state stored in a ref so it persists across view switches
interface ToolCallRecord {
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_output: string;
  connector?: string;
}

interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  model?: string;
}

interface ConvStreamState {
  messages: Message[];
  loading: boolean;
  streamStatus: string | null;
  toolsUsed: boolean;
  abortController: AbortController | null;
  toolCalls: ToolCallRecord[];
  tokenUsage: TokenUsage | null;
}

/** Generate 2-3 follow-up suggestion chips from assistant response content */
const FollowUpSuggestions = ({ content, onSend }: { content: string; onSend: (msg: string) => void }) => {
  const suggestions = useMemo(() => {
    const result: { icon?: string; text: string }[] = [];
    const general = isGeneralResponse(content);

    // Try to extract topics/keywords from the response
    const topicPatterns = [
      /\b(?:total|average|count|sum|revenue|sales|growth|trend|comparison|breakdown)\b/gi,
      /\b(?:by\s+\w+|per\s+\w+|across\s+\w+|over\s+\w+)\b/gi,
    ];

    const topics = new Set<string>();
    for (const pattern of topicPatterns) {
      const matches = content.match(pattern);
      if (matches) matches.slice(0, 3).forEach((m) => topics.add(m.trim().toLowerCase()));
    }

    // Data-oriented suggestions
    if (content.includes("table") || content.includes("data") || content.includes("row")) {
      result.push({ text: "Can you visualize this data as a chart?" });
    }
    if (topics.size > 0) {
      const topicArr = Array.from(topics);
      result.push({ text: `Tell me more about ${topicArr[0]}` });
      if (topicArr.length > 1) {
        result.push({ text: `How does ${topicArr[0]} compare to ${topicArr[1]}?` });
      }
    }
    if (!general && content.length > 300) {
      result.push({ text: "Summarize the key takeaways" });
    }

    // For general/non-data responses, add capability suggestions
    if (general || result.length === 0) {
      // Pick 3 random capability follow-ups that aren't already added
      const existing = new Set(result.map((r) => r.text));
      const available = CAPABILITY_FOLLOWUPS.filter((c) => !existing.has(c.text));
      const shuffled = [...available].sort(() => Math.random() - 0.5);
      for (const cap of shuffled.slice(0, Math.max(0, 4 - result.length))) {
        result.push({ icon: cap.icon, text: cap.text });
      }
    }

    // Ensure at least 2 suggestions
    if (result.length < 2) {
      result.push({ text: "Can you elaborate on this?" });
    }
    if (result.length < 3) {
      result.push({ text: "What other insights can you find?" });
    }

    return result.slice(0, 4);
  }, [content]);

  if (suggestions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-4 py-3">
      {suggestions.map((s, i) => (
        <button
          key={i}
          onClick={() => onSend(s.text)}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-accent hover:border-primary/30"
        >
          {s.icon ? <span className="text-xs">{s.icon}</span> : <Sparkles className="h-3 w-3 text-primary" />}
          {s.text}
        </button>
      ))}
    </div>
  );
};

const Chat = () => {
  const { user, profile, clientInfo, markHasData } = useAuth();
  const { toast } = useToast();
  const { log: auditLog } = useAuditLog();
  const { maskText, requestUnmask } = usePiiMasking();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { setMobileChatMode } = useMobileChatMode();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [mobileInputOpen, setMobileInputOpen] = useState(false);

  // Map of conversation ID -> streaming state (for background streams)
  const streamStates = useRef<Map<string, ConvStreamState>>(new Map());
  // Track which conversation the user is currently viewing
  const activeConvRef = useRef<string | null>(null);
  const [streamingConvIds, setStreamingConvIds] = useState<string[]>([]);

  const [suggestions, setSuggestions] = useState<SuggestionItem[]>(DEFAULT_SUGGESTIONS);
  /** messageId to show in the per-message debug dialog; null = closed */
  const [debugMessageId, setDebugMessageId] = useState<string | null>(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  // Research template browser state
  const [researchTemplateBrowserOpen, setResearchTemplateBrowserOpen] = useState(false);
  const [selectedResearchTemplate, setSelectedResearchTemplate] = useState<WorkflowTemplate | null>(null);
  const [researchTemplatePromptOpen, setResearchTemplatePromptOpen] = useState(false);

  // Only check schema when profile.has_data is not yet true (optimization)
  const shouldCheckSchema = !profile?.has_data;
  const { tables: schemaTables, isLoading: schemaLoading } = useMcpSchema();

  // Determine if user has no data at all
  const hasNoData = shouldCheckSchema && !schemaLoading && schemaTables.length === 0 && dataSources.length === 0;

  // Auto-mark has_data when we detect data exists
  useEffect(() => {
    if (shouldCheckSchema && !schemaLoading && (schemaTables.length > 0 || dataSources.length > 0)) {
      markHasData();
    }
  }, [shouldCheckSchema, schemaLoading, schemaTables.length, dataSources.length, markHasData]);

  // Fetch chat suggestions from client template or fallback to "default" template
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!user) return;
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("client_id")
          .eq("id", user.id)
          .single();

        let templateIds: string[] = [];

        if (profile?.client_id) {
          const { data: client } = await supabase
            .from("client")
            .select("template_list")
            .eq("id", profile.client_id)
            .single();

          if (client?.template_list && (client.template_list as string[]).length > 0) {
            templateIds = client.template_list as string[];
          }
        }

        if (templateIds.length > 0) {
          const { data: templates } = await supabase
            .from("template")
            .select("chat_suggestions")
            .in("id", templateIds);

          if (templates && templates.length > 0) {
            const allSuggestions = templates.flatMap((t) => t.chat_suggestions || []);
            if (allSuggestions.length > 0) {
              setSuggestions(
                allSuggestions.map((text, i) => ({
                  icon: SUGGESTION_ICONS[i % SUGGESTION_ICONS.length],
                  text,
                }))
              );
              return;
            }
          }
        }

        const { data: defaultTemplate } = await supabase
          .from("template")
          .select("chat_suggestions")
          .ilike("name", "default")
          .limit(1)
          .single();

        if (defaultTemplate?.chat_suggestions && (defaultTemplate.chat_suggestions as string[]).length > 0) {
          setSuggestions(
            (defaultTemplate.chat_suggestions as string[]).map((text, i) => ({
              icon: SUGGESTION_ICONS[i % SUGGESTION_ICONS.length],
              text,
            }))
          );
        } else {
          setSuggestions(DEFAULT_SUGGESTIONS);
        }
      } catch {
        setSuggestions(DEFAULT_SUGGESTIONS);
      }
    };
    fetchSuggestions();
  }, [user]);

  const [dashboard, setDashboard] = useState<DashboardSpec | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [savedDashboards, setSavedDashboards] = useState<DashboardSnapshot[]>([]);
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
  const [subscribeSnapshotId, setSubscribeSnapshotId] = useState<string | null>(null);
  const [subscribeSnapshotTitle, setSubscribeSnapshotTitle] = useState("");

  // Helper: sync view state from a background stream state
  const syncViewFromStream = useCallback((convId: string) => {
    const state = streamStates.current.get(convId);
    if (state) {
      setMessages([...state.messages]);
      setLoading(state.loading);
      setStreamStatus(state.streamStatus);
    }
  }, []);

  // Update activeConvRef when conversationId changes
  useEffect(() => {
    activeConvRef.current = conversationId;
  }, [conversationId]);

  // Realtime subscription: watch for message UPDATEs (pending → complete) in the
  // active conversation so the UI updates when the user returns to the tab.
  useEffect(() => {
    if (!conversationId) return;

    const channel = supabase
      .channel(`conv-messages:${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          // Merge the updated content and status into our local message list
          setMessages((prev) =>
            prev.map((m) =>
              m.id === updated.id
                ? {
                    ...m,
                    content: updated.content ?? m.content,
                    status: updated.status ?? m.status,
                    // Mark usedTools if the update didn't come from an active stream
                    // (we can't re-check message_actions here; keep existing value)
                  }
                : m,
            ),
          );
          // If the conversation finished while we were away, clear the loading state
          if (updated.status === "complete" || updated.status === "error") {
            const activeStream = streamStates.current.get(conversationId);
            if (!activeStream?.loading) {
              setLoading(false);
              setStreamStatus(null);
            }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Fetch available data sources
  useEffect(() => {
    const fetchDataSources = async () => {
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("client_id")
        .eq("id", user.id)
        .single();

      const clientId = profile?.client_id;

      let query = supabase
        .from("data_sources")
        .select("id, name, enabled")
        .eq("enabled", true)
        .order("name");

      if (clientId) {
        query = query.eq("client_id", clientId);
      }

      const { data } = await query;
      const sources = (data as unknown as DataSource[]) || [];

      setDataSources(sources);
      setSelectedSourceIds(sources.map((s) => s.id));
    };
    fetchDataSources();
  }, [user]);

  // Helper to update URL params for conv and dashboard
  const updateUrlParams = useCallback((convId: string | null, dashId: string | null) => {
    const params: Record<string, string> = {};
    if (convId) params.conv = convId;
    if (dashId) params.dash = dashId;
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Deep-link: if ?conv=<id> is in the URL, open that conversation
  useEffect(() => {
    const convParam = searchParams.get("conv");
    const dashParam = searchParams.get("dash");
    if (!convParam) return;
    selectConversation(convParam, dashParam);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile bottom-nav: handle "New" (resetChat) and "Research" (openSidebar) navigation states
  useEffect(() => {
    if (location.state?.resetChat) {
      newConversation();
      setSidebarOpen(false);
    }
    if (location.state?.openSidebar) {
      setSidebarOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.resetChat, location.state?.openSidebar]);

  // Activate mobile chat mode when viewing an existing conversation with messages
  const hasActiveChat = isMobile && conversationId && messages.length > 0;
  useEffect(() => {
    if (hasActiveChat) {
      setMobileChatMode(true, conversationTitle);
    } else {
      setMobileChatMode(false);
    }
    return () => setMobileChatMode(false);
  }, [hasActiveChat, conversationTitle, setMobileChatMode]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && conversationId) {
        const state = streamStates.current.get(conversationId);
        if (state?.abortController) {
          state.abortController.abort();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [conversationId]);

  const loadDashboards = async (convId: string) => {
    const { data } = await supabase
      .from("dashboard_snapshots")
      .select("id, title, spec, created_at, updated_at, message_id")
      .eq("conversation_id", convId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (data) {
      setSavedDashboards(data as unknown as DashboardSnapshot[]);
    } else {
      setSavedDashboards([]);
    }
  };

  const loadMessages = async (convId: string) => {
    // If there's an active stream for this conversation, use its messages
    const existingState = streamStates.current.get(convId);
    if (existingState) {
      setMessages([...existingState.messages]);
      setLoading(existingState.loading);
      setStreamStatus(existingState.streamStatus);
      return;
    }

    const { data } = await supabase
      .from("messages")
      .select("id, role, content, created_at, status")
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });
    if (data) {
      const assistantIds = data.filter((m) => m.role === "assistant").map((m) => m.id);
      let toolMessageIds = new Set<string>();
      if (assistantIds.length > 0) {
        const { data: actions } = await supabase
          .from("message_actions")
          .select("message_id")
          .in("message_id", assistantIds);
        if (actions) toolMessageIds = new Set(actions.map((a) => a.message_id));
      }

      // Filter out stuck pending messages (empty content, older than 5 min)
      const now = Date.now();
      const STUCK_THRESHOLD_MS = 5 * 60 * 1000;

      setMessages(data
        .filter((m) => {
          if ((m as any).status === "pending" && !m.content) {
            const age = now - new Date(m.created_at).getTime();
            return age < STUCK_THRESHOLD_MS; // keep only recent pending (still streaming)
          }
          return true;
        })
        .map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          usedTools: toolMessageIds.has(m.id),
          createdAt: m.created_at,
          status: (m as any).status ?? "complete",
        })));
    }
    setLoading(false);
    setStreamStatus(null);
  };

  const loadConversationSources = async (convId: string) => {
    const { data } = await supabase
      .from("conversations")
      .select("selected_data_source_ids")
      .eq("id", convId)
      .single();
    if (data?.selected_data_source_ids && (data.selected_data_source_ids as string[]).length > 0) {
      setSelectedSourceIds(data.selected_data_source_ids as string[]);
    } else {
      setSelectedSourceIds(dataSources.map((s) => s.id));
    }
  };

  const selectConversation = async (id: string, dashParam?: string | null) => {
    setConversationId(id);
    setMobileInputOpen(false);
    updateUrlParams(id, dashParam || null);
    setDashboard(null);
    setDashboardOpen(false);
    setFocusTrigger((p) => p + 1);
    setActiveDashboardId(null);
    // Load bookmark state + title
    supabase.from("conversations").select("bookmarked, title").eq("id", id).single().then(({ data }) => {
      setBookmarked(!!(data as any)?.bookmarked);
      setConversationTitle((data as any)?.title ?? "");
      auditLog({ action: "accessed", resourceType: "chat", resourceId: id, resourceName: (data as any)?.title ?? undefined });
    });
    loadMessages(id);
    loadConversationSources(id);
    await loadDashboards(id);

    // If a dashboard ID was provided via URL, open it after dashboards load
    if (dashParam) {
      // We need to fetch the specific dashboard since loadDashboards sets state async
      const { data: snap } = await supabase
        .from("dashboard_snapshots")
        .select("id, title, spec, created_at, updated_at, message_id")
        .eq("id", dashParam)
        .single();
      if (snap) {
        const s = snap as unknown as DashboardSnapshot;
        setDashboard({
          title: s.title,
          widgets: s.spec.widgets || [],
          savedToolCalls: s.spec.savedToolCalls || [],
          dataSourceIds: s.spec.dataSourceIds,
          updatedAt: s.updated_at || s.created_at,
          configuredFilters: s.spec.configuredFilters || [],
        });
        setActiveDashboardId(s.id);
        setDashboardOpen(true);
      }
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setConversationTitle("");
    setMobileInputOpen(false);
    updateUrlParams(null, null);
    setMessages([]);
    setLoading(false);
    if (!isMobile) setFocusTrigger((p) => p + 1);
    setStreamStatus(null);
    setSelectedSourceIds(dataSources.map((s) => s.id));
    setDashboard(null);
    setDashboardOpen(false);
    setSavedDashboards([]);
    setActiveDashboardId(null);
    setBookmarked(false);
  };

  const toggleBookmark = async () => {
    if (!conversationId) return;
    const newVal = !bookmarked;
    setBookmarked(newVal);
    await supabase.from("conversations").update({ bookmarked: newVal } as any).eq("id", conversationId);
  };

  const openSavedDashboard = (snap: DashboardSnapshot) => {
    setDashboard({
      title: snap.title,
      widgets: snap.spec.widgets || [],
      savedToolCalls: snap.spec.savedToolCalls || [],
      dataSourceIds: snap.spec.dataSourceIds,
      updatedAt: snap.updated_at || snap.created_at,
      configuredFilters: snap.spec.configuredFilters || [],
    });
    setActiveDashboardId(snap.id);
    setDashboardOpen(true);
    updateUrlParams(conversationId, snap.id);
  };

  const handleSaveFilters = async (result: FilterSuggestionsResult) => {
    if (!activeDashboardId) return;
    const snap = savedDashboards.find((d) => d.id === activeDashboardId);
    if (!snap) return;

    const { filters, updatedWidgets } = result;

    // Merge updated SQL queries into existing widgets
    const currentWidgets = snap.spec?.widgets || [];
    const mergedWidgets = currentWidgets.map((w: any, i: number) => {
      const updated = updatedWidgets[i];
      if (updated && updated.sqlQuery !== w.sqlQuery) {
        return { ...w, sqlQuery: updated.sqlQuery };
      }
      return w;
    });

    const updatedSpec = { ...snap.spec, widgets: mergedWidgets, configuredFilters: filters };
    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString() } as any)
      .eq("id", activeDashboardId);

    if (error) {
      toast({ title: "Error", description: "Failed to save filters", variant: "destructive" });
      return;
    }

    setSavedDashboards((prev) =>
      prev.map((d) => (d.id === activeDashboardId ? { ...d, spec: updatedSpec } : d))
    );
    setDashboard((prev) => prev ? { ...prev, widgets: mergedWidgets, configuredFilters: filters } : prev);
    toast({
      title: "Filters saved",
      description: `${filters.length} filter${filters.length !== 1 ? "s" : ""} configured. Queries updated with filter placeholders.`,
    });
  };

  const deleteDashboard = async (id: string) => {
    // Soft-delete: mark as deleted instead of removing
    await supabase.from("dashboard_snapshots").update({ deleted_at: new Date().toISOString(), deleted_by: user?.id } as any).eq("id", id);
    setSavedDashboards((prev) => prev.filter((d) => d.id !== id));
    if (activeDashboardId === id) {
      setDashboard(null);
      setDashboardOpen(false);
      setActiveDashboardId(null);
      updateUrlParams(conversationId, null);
    }
    toast({ title: "Deleted", description: "Dashboard moved to recycle bin" });
  };

  const generateDashboard = async (content: string, messageId?: string) => {
    setDashboardOpen(true);
    setDashboardLoading(true);
    setDashboard(null);
    setActiveDashboardId(null);

    // Collect MCP tool calls from the current conversation's stream state so they
    // can be saved alongside the dashboard for filter-driven refresh.
    const convToolCalls = conversationId
      ? (streamStates.current.get(conversationId)?.toolCalls ?? [])
      : [];
    // Normalise: keep only tool_name + tool_input (drop tool_output to save space)
    const savedToolCalls = convToolCalls.map((tc) => ({
      tool_name: tc.tool_name,
      tool_input: tc.tool_input,
    }));

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/generate-dashboard`, {
        method: "POST",
        headers,
        body: JSON.stringify({ content, toolCalls: savedToolCalls }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: "Failed to generate dashboard" }));
        throw new Error(errData.error || "Failed to generate dashboard");
      }

      const raw = await res.json();
      const { _usage, ...spec } = raw;

      // Raw SQL is stored directly in each widget's sqlQuery field.
      // Set _queryName from the title for display purposes.
      (spec.widgets || []).forEach((w: any) => {
        if (w.sqlQuery?.trim()) {
          w._queryName = w.title || "Dashboard Query";
        }
      });

      // Attach tool calls and data source IDs so the panel can offer refresh/filters
      const fullSpec: DashboardSpec = {
        ...spec,
        savedToolCalls,
        dataSourceIds: [...selectedSourceIds],
      };
      setDashboard(fullSpec);

      if (conversationId) {
        const { data: saved, error: insertError } = await supabase
          .from("dashboard_snapshots")
          .insert({
            conversation_id: conversationId,
            title: spec.title || "Dashboard",
            spec: {
              widgets: spec.widgets,
              savedToolCalls,
              dataSourceIds: [...selectedSourceIds],
            } as any,
            client_id: profile?.client_id ?? "",
            input_tokens: _usage?.input_tokens ?? null,
            output_tokens: _usage?.output_tokens ?? null,
            total_tokens: _usage?.total_tokens ?? null,
            model: _usage?.model ?? null,
            message_id: messageId ?? null,
          } as any)
          .select("id, title, spec, created_at, updated_at, message_id")
          .single();
        if (insertError) {
          console.error("Dashboard snapshot insert error:", insertError);
        }
        if (saved) {
          const snap = saved as unknown as DashboardSnapshot;
          setSavedDashboards((prev) => [snap, ...prev]);
          setActiveDashboardId(snap.id);
          updateUrlParams(conversationId, snap.id);

        }
      } else {
        console.warn("Dashboard not saved: no conversationId");
      }
    } catch (error: any) {
      console.error("Dashboard generation error:", error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setDashboardOpen(false);
    } finally {
      setDashboardLoading(false);
    }
  };

  const sendMessage = async (content: string) => {
    if (!user) return;

    // Capture current source IDs at send time
    const currentSourceIds = [...selectedSourceIds];
    const userMsg: Message = { role: "user", content };

    // Determine or create conversation
    let convId = conversationId;

    if (!convId) {
      const title = content.slice(0, 60) + (content.length > 60 ? "..." : "");
      const persistableSourceIds = currentSourceIds;
      try {
          const { data, error } = await supabase
            .from("conversations")
            .insert({ user_id: user.id, title, selected_data_source_ids: persistableSourceIds, client_id: profile?.client_id ?? "", profile_name: profile?.full_name ?? null } as any)
            .select("id")
            .single();
        if (error) throw error;
        convId = data.id;
        setConversationId(convId);
        setConversationTitle(title);
        updateUrlParams(convId, null);
        // Update the ref immediately so view-update checks below see the new ID
        // before the async state update propagates via useEffect.
        activeConvRef.current = convId;
        auditLog({ action: "created", resourceType: "chat", resourceId: convId, resourceName: title });
      } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
    }

    // Initialize stream state for this conversation
    const existingState = streamStates.current.get(convId);
    const prevMessages = existingState ? [...existingState.messages] : [...messages];
    const allMessages = [...prevMessages, userMsg];

    const streamState: ConvStreamState = {
      messages: [...allMessages, { role: "assistant", content: "", usedTools: false }],
      loading: true,
      streamStatus: null,
      toolsUsed: false,
      abortController: new AbortController(),
      toolCalls: [],
      tokenUsage: null,
    };
    streamStates.current.set(convId, streamState);
    setStreamingConvIds((prev) => [...prev, convId]);

    // Update view if this is the active conversation
    if (activeConvRef.current === convId) {
      setMessages([...streamState.messages]);
      setLoading(true);
      setStreamStatus(null);
    }

    // Helper to update stream state and optionally sync view
    const updateState = (convIdTarget: string, updater: (s: ConvStreamState) => void) => {
      const s = streamStates.current.get(convIdTarget);
      if (!s) return;
      updater(s);
      if (activeConvRef.current === convIdTarget) {
        setMessages([...s.messages]);
        setLoading(s.loading);
        setStreamStatus(s.streamStatus);
      }
    };

    try {
      await supabase.from("messages").insert({ conversation_id: convId, role: "user", content, client_id: profile?.client_id ?? "" });
      await supabase.from("conversations").update({ updated_at: new Date().toISOString() }).eq("id", convId);

      const history = allMessages.map((m) => ({ role: m.role, content: m.content }));

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Ensure we have a valid session before calling the edge function
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        // Try to refresh the session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session) {
          throw new Error("Your session has expired. Please refresh the page and log in again.");
        }
      }

      const headers = await buildEdgeFunctionHeaders();

      const res = await fetch(`${supabaseUrl}/functions/v1/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          messages: history,
          dataSourceIds: currentSourceIds,
          conversationId: convId,
          clientId: profile?.client_id ?? "",
        }),
        signal: streamState.abortController!.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: "Failed to get response" }));
        throw new Error(errData.error || "Failed to get response");
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";
      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;
          const payload = trimmed.slice(6);
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.assistantMessageId) {
              // Server created a pending DB row; store its ID so Realtime can
              // update the message if the user navigates away mid-stream.
              updateState(convId!, (s) => {
                const lastMsg = s.messages[s.messages.length - 1];
                if (lastMsg?.role === "assistant") {
                  lastMsg.id = parsed.assistantMessageId;
                  lastMsg.status = "pending";
                }
              });
            }
            if (parsed.token) {
              fullContent += parsed.token;
              updateState(convId!, (s) => {
                s.streamStatus = null;
                s.messages[s.messages.length - 1] = {
                  ...s.messages[s.messages.length - 1],
                  role: "assistant",
                  content: fullContent,
                  usedTools: s.toolsUsed,
                  status: "processing",
                };
              });
            }
            if (parsed.status) {
              updateState(convId!, (s) => {
                s.toolsUsed = true;
                s.streamStatus = parsed.status;
              });
            }
            if (parsed.tool_call) {
              updateState(convId!, (s) => {
                s.toolCalls.push(parsed.tool_call);
              });
              // message_actions are saved server-side by the chat edge function
              // in the finally block. The SSE tool_call event is only for UI.
            }
            if (parsed.usage) {
              updateState(convId!, (s) => {
                s.tokenUsage = parsed.usage;
              });
            }
            if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e: any) {
            if (e.message && e.message !== "Unexpected end of JSON input") {
              if (e.message !== e.message.match(/JSON/)?.[0]) throw e;
            }
          }
        }
      }

      if (!fullContent) {
        fullContent = "Sorry, I couldn't generate a response.";
      }
      // Mark the last assistant message as complete in local state.
      // The server has already persisted it via the pending message UPDATE.
      updateState(convId!, (s) => {
        const lastMsg = s.messages[s.messages.length - 1];
        s.messages[s.messages.length - 1] = {
          ...lastMsg,
          role: "assistant",
          content: fullContent,
          usedTools: s.toolsUsed,
          status: "complete",
        };
      });
      // No frontend INSERT needed — the edge function UPDATEs the pending message
    } catch (error: any) {
      if (error.name === "AbortError") {
        // User pressed Escape. The edge function continues running via waitUntil
        // and will UPDATE the pending message with the full content when done.
        // Realtime will push the update to the frontend automatically.
      } else {
        console.error("Send error:", error);
        let userMessage = error.message;
        // "Failed to fetch" is a generic network error — provide a more helpful message
        if (error instanceof TypeError && error.message === "Failed to fetch") {
          userMessage = "Network error: Unable to reach the server. Please check your connection and try again.";
        }
        toast({ title: "Error", description: userMessage, variant: "destructive" });
        updateState(convId!, (s) => {
          s.messages = s.messages.slice(0, -1);
        });
      }
    } finally {
      updateState(convId!, (s) => {
        s.loading = false;
        s.streamStatus = null;
        s.abortController = null;
      });
      // Clean up stream state after completion
      setStreamingConvIds((prev) => prev.filter((id) => id !== convId));
      setTimeout(() => {
        const s = streamStates.current.get(convId!);
        if (s && !s.loading) {
          streamStates.current.delete(convId!);
        }
      }, 500);
    }
  };

  // Build a set of message IDs that have associated dashboards
  const dashboardByMessageId = new Map<string, DashboardSnapshot>();
  for (const snap of savedDashboards) {
    if (snap.message_id && !dashboardByMessageId.has(snap.message_id)) {
      dashboardByMessageId.set(snap.message_id, snap);
    }
  }

  const viewDashboardByMessageId = (msgId: string) => {
    const snap = dashboardByMessageId.get(msgId);
    if (snap) openSavedDashboard(snap);
  };

  const handleOpenInBuilder = (snapshotId: string) => {
    navigate(`/dashboards?dashboard=${snapshotId}`);
  };

  const handleDownloadPdf = async (snapshotId: string, title: string) => {
    try {
      // If the dashboard panel is not visible for this snapshot, open it first
      if (!dashboardOpen || activeDashboardId !== snapshotId) {
        const snap = savedDashboards.find((s) => s.id === snapshotId);
        if (snap) openSavedDashboard(snap);
        // Give the panel time to render (charts & widgets may need extra time)
        await new Promise((r) => setTimeout(r, 1500));
      }

      const dashboardEl = document.querySelector("[data-dashboard-panel]") as HTMLElement | null;
      if (!dashboardEl) {
        toast({ title: "Error", description: "Dashboard panel not found. Please open the dashboard first.", variant: "destructive" });
        return;
      }

      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Temporarily expand scrollable containers so html2canvas captures ALL content
      const scrollContainer = dashboardEl.querySelector(".overflow-y-auto") as HTMLElement | null;
      const savedStyles: { el: HTMLElement; overflow: string; height: string; maxHeight: string; flex: string }[] = [];

      // Remove height constraints on the panel itself
      savedStyles.push({
        el: dashboardEl,
        overflow: dashboardEl.style.overflow,
        height: dashboardEl.style.height,
        maxHeight: dashboardEl.style.maxHeight,
        flex: dashboardEl.style.flex,
      });
      dashboardEl.style.overflow = "visible";
      dashboardEl.style.height = "auto";
      dashboardEl.style.maxHeight = "none";
      dashboardEl.style.flex = "none";

      // Remove scroll / overflow clipping on the content container
      if (scrollContainer) {
        savedStyles.push({
          el: scrollContainer,
          overflow: scrollContainer.style.overflow,
          height: scrollContainer.style.height,
          maxHeight: scrollContainer.style.maxHeight,
          flex: scrollContainer.style.flex,
        });
        scrollContainer.style.overflow = "visible";
        scrollContainer.style.height = "auto";
        scrollContainer.style.maxHeight = "none";
        scrollContainer.style.flex = "none";
      }

      // Allow the layout to reflow
      await new Promise((r) => setTimeout(r, 300));

      const canvas = await html2canvas(dashboardEl, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        windowHeight: dashboardEl.scrollHeight,
      });

      // Restore original styles
      for (const s of savedStyles) {
        s.el.style.overflow = s.overflow;
        s.el.style.height = s.height;
        s.el.style.maxHeight = s.maxHeight;
        s.el.style.flex = s.flex;
      }

      const imgData = canvas.toDataURL("image/png");
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // A4 dimensions in points (72 dpi)
      const a4Width = 841.89; // A4 landscape width in points
      const a4Height = 595.28; // A4 landscape height in points
      const margin = 20;

      const usableWidth = a4Width - margin * 2;
      const usableHeight = a4Height - margin * 2;

      // Scale to fit the width of A4 landscape
      const scaleFactor = usableWidth / canvasWidth;
      const scaledHeight = canvasHeight * scaleFactor;

      // Calculate how many pages are needed
      const totalPages = Math.ceil(scaledHeight / usableHeight);

      const pdf = new jsPDF("l", "pt", "a4");

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage("a4", "l");

        // Calculate source slice from the canvas for this page
        const srcY = Math.round((page * usableHeight) / scaleFactor);
        const srcH = Math.min(
          Math.round(usableHeight / scaleFactor),
          canvasHeight - srcY
        );

        // Create a sub-canvas for this page slice
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = srcH;
        const ctx = pageCanvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0, srcY, canvasWidth, srcH,
            0, 0, canvasWidth, srcH,
          );
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const drawHeight = srcH * scaleFactor;
        pdf.addImage(pageImgData, "PNG", margin, margin, usableWidth, drawHeight);
      }

      pdf.save(`${title.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`);

      toast({ title: "Downloaded", description: "Dashboard PDF saved" });
    } catch (err: any) {
      console.error("PDF download error:", err);
      toast({ title: "Error", description: "Failed to download PDF", variant: "destructive" });
    }
  };

  // Check if any conversation is loading (for showing indicator on sidebar etc.)
  const currentConvLoading = conversationId
    ? streamStates.current.get(conversationId)?.loading || loading
    : loading;

  const handleMobileBack = () => {
    newConversation();
    navigate("/conversations");
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden">
      {/* Mobile chat header – shown when viewing an existing conversation on mobile */}
      {hasActiveChat && (
        <MobileChatHeader
          title={conversationTitle}
          onBack={handleMobileBack}
          bookmarked={bookmarked}
          onToggleBookmark={toggleBookmark}
        />
      )}
      <div className="flex flex-1 min-h-0 w-full overflow-hidden">
      {/* Desktop sidebar toggle – hidden on mobile (bottom nav handles navigation) */}
      {false && (
        <Button
          variant="ghost"
          size="icon"
          className="fixed left-3 top-3 z-50 hidden"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Sidebar – desktop only; completely hidden on mobile */}
      <div
        className={`hidden md:block fixed left-0 z-40 transition-all duration-300 md:relative md:inset-auto md:z-auto top-14 bottom-16 md:top-0 md:bottom-0 ${
          sidebarOpen
            ? "translate-x-0 md:translate-x-0 md:w-64"
            : "-translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden"
        }`}
      >
        <ChatSidebar
          activeId={conversationId}
          onSelect={selectConversation}
          onNew={newConversation}
          onToggle={() => setSidebarOpen(false)}
          streamingConvIds={streamingConvIds}
        />
      </div>

      {/* Desktop expand button */}
      {!sidebarOpen && (
        <button
          className="hidden md:flex items-center justify-center self-stretch w-6 shrink-0 border-r border-border bg-sidebar hover:bg-sidebar-accent transition-colors"
          onClick={() => setSidebarOpen(true)}
          title="Expand sidebar"
        >
          <ChevronRight className="h-4 w-4 text-sidebar-foreground" />
        </button>
      )}

      {/* Right section: chat + optional dashboard split */}
      <div className="flex flex-1 min-w-0 h-full overflow-hidden w-full">
        {/* Main chat area */}
        <div className={cn("flex flex-col min-w-0 h-full overflow-hidden", DASHBOARD_ON_CHAT && dashboardOpen ? "w-1/2 shrink-0" : "flex-1 w-full")}>
          {/* Dashboard bar */}
          {DASHBOARD_ON_CHAT && savedDashboards.length > 0 && (
            <div className="flex items-center gap-2 border-b border-border px-4 py-2 overflow-x-auto">
              <LayoutDashboard className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground shrink-0">Dashboards:</span>
              {savedDashboards.map((snap) => (
                <button
                  key={snap.id}
                  onClick={() => openSavedDashboard(snap)}
                  className={`flex shrink-0 flex-col items-start rounded-lg border px-3 py-1.5 text-left transition-colors ${
                    activeDashboardId === snap.id
                      ? "border-primary/50 bg-secondary"
                      : "border-border bg-card hover:bg-accent"
                  }`}
                >
                  <span className="text-xs font-medium leading-tight">{snap.title}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {format(new Date(snap.created_at), "MMM d, h:mm a")}
                  </span>
                </button>
              ))}
            </div>
          )}

          {messages.length === 0 && !loading ? (
            <div className="flex flex-1 flex-col items-center overflow-y-auto px-4 py-8">
              <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4">
                <div className="flex h-32 w-32 items-center justify-center rounded-2xl">
                  <img src={clientInfo?.logo_url || databullLogo} alt={APP_NAME} className="h-28 w-28 rounded-2xl object-cover" />
                </div>
                <h1 className="text-2xl font-semibold">How can I help you today?</h1>
                <p className="text-muted-foreground text-center max-w-md">
                  Ask me anything about your data, or explore the capabilities below.
                </p>

                {/* No data banner */}
                {hasNoData && (
                  <div className="w-full max-w-xl rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/50 p-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                        Please upload data or connect your data sources
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        To start asking questions, you need to connect at least one data source or upload a file.{" "}
                        <button
                          onClick={() => setHowItWorksOpen(true)}
                          className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium"
                        >
                          How does it work?
                        </button>
                      </p>
                    </div>
                  </div>
                )}

                {/* Quick start suggestions */}
                {SUGGESTIONS_ON_CHAT && (
                  <div className="mt-4 grid w-full max-w-xl grid-cols-2 gap-2">
                    {suggestions.map((prompt) => (
                      <button
                        key={prompt.text}
                        onClick={() => sendMessage(prompt.text)}
                        className="flex items-center gap-2.5 rounded-xl border border-border bg-card p-3 text-left text-sm text-foreground transition-colors hover:bg-accent"
                      >
                        <span className="text-base">{prompt.icon}</span>
                        <span>{prompt.text}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Research Templates button */}
                <div className="mt-4 w-full max-w-xl flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setResearchTemplateBrowserOpen(true)}
                    className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                  >
                    <LayoutTemplate className="h-4 w-4" />
                    Research Templates
                  </Button>
                </div>

                {/* Capability discovery section */}
                <div className="mt-8 w-full max-w-xl">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold text-foreground">Explore what I can do</h2>
                  </div>
                  <div className="space-y-4">
                    {CAPABILITY_CATEGORIES.map((cat) => (
                      <div key={cat.title}>
                        <h3 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                          <span>{cat.emoji}</span>
                          {cat.title}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {cat.items.map((item) => (
                            <button
                              key={item.text}
                              onClick={() => sendMessage(item.text)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-accent hover:border-primary/30"
                            >
                              <span>{item.icon}</span>
                              <span>{item.text}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div ref={scrollRef} className="flex-1 overflow-y-auto">
              <div className="mx-auto max-w-3xl">
                {messages.map((msg, i) => {
                  const isPendingMsg = msg.role === "assistant" && (msg.status === "pending" || msg.status === "processing");
                  return (
                    <ChatMessage
                      key={msg.id ?? i}
                      role={msg.role}
                      content={msg.content}
                      usedTools={msg.usedTools}
                      createdAt={msg.createdAt}
                      isStreaming={
                        (loading && i === messages.length - 1 && msg.role === "assistant") ||
                        // Show streaming indicator for pending messages loaded from DB
                        // (e.g. user navigated back while processing)
                        (isPendingMsg && !loading)
                      }
                      messageId={msg.id}
                      onGenerateDashboard={DASHBOARD_ON_CHAT ? generateDashboard : undefined}
                      hasDashboard={DASHBOARD_ON_CHAT ? !!msg.id && dashboardByMessageId.has(msg.id) : undefined}
                      dashboardTitle={DASHBOARD_ON_CHAT && msg.id ? dashboardByMessageId.get(msg.id)?.title : undefined}
                      onViewDashboard={DASHBOARD_ON_CHAT ? viewDashboardByMessageId : undefined}
                      dashboardSnapshotId={DASHBOARD_ON_CHAT && msg.id ? dashboardByMessageId.get(msg.id)?.id : undefined}
                      onSubscribeDashboard={DASHBOARD_ON_CHAT ? (snapId, title) => {
                        setSubscribeSnapshotId(snapId);
                        setSubscribeSnapshotTitle(title);
                      } : undefined}
                      onDebug={msg.role === "assistant" && msg.id ? () => setDebugMessageId(msg.id!) : undefined}
                      onOpenInBuilder={DASHBOARD_ON_CHAT ? handleOpenInBuilder : undefined}
                      onDownloadPdf={DASHBOARD_ON_CHAT ? handleDownloadPdf : undefined}
                      maskText={maskText}
                      onRequestUnmask={requestUnmask}
                    />
                  );
                })}
                {loading && !messages[messages.length - 1]?.content && (
                  <div className="flex gap-4 px-4 py-6">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg">
                      <img src={clientInfo?.logo_url || databullLogo} alt={APP_NAME} className="h-7 w-7 rounded-lg object-cover" />
                    </div>
                    <div className="flex items-center gap-2">
                      {streamStatus ? (
                        <Badge variant="secondary" className="gap-1.5 animate-pulse">
                          <Wrench className="h-3 w-3" />
                          {streamStatus}
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <div className="flex gap-1">
                            <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
                            <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
                            <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
                          </div>
                          <span className="text-xs text-muted-foreground ml-2">Thinking</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {loading && streamStatus && messages[messages.length - 1]?.content && (
                  <div className="flex justify-center px-4 pb-4">
                    <Badge variant="secondary" className="gap-1.5 animate-pulse">
                      <Wrench className="h-3 w-3" />
                      {streamStatus}
                    </Badge>
                  </div>
                )}
                {/* Retry button when the last message is from the user with no assistant reply */}
                {!loading && messages.length > 0 && messages[messages.length - 1]?.role === "user" && (
                  <div className="flex justify-center px-4 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 text-xs"
                      onClick={() => sendMessage(messages[messages.length - 1].content)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Retry
                    </Button>
                  </div>
                )}
                {/* Follow-up suggestion chips */}
                {SUGGESTIONS_ON_CHAT && !loading && messages.length > 0 && messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.content && (
                  <FollowUpSuggestions
                    content={messages[messages.length - 1].content}
                    onSend={sendMessage}
                  />
                )}
              </div>
            </div>
          )}

          {/* On mobile with active chat: only show input when mobileInputOpen; on desktop/new chat: always show */}
          {(!hasActiveChat || mobileInputOpen || !isMobile) && (
            <ChatInput
              onSend={(msg) => { sendMessage(msg); if (isMobile) setMobileInputOpen(false); }}
              disabled={false}
              loading={loading}
              onStop={() => {
                if (conversationId) {
                  const state = streamStates.current.get(conversationId);
                  state?.abortController?.abort();
                }
              }}
              dataSources={dataSources}
              selectedSourceIds={selectedSourceIds}
              onSelectedSourceIdsChange={(ids) => {
                setSelectedSourceIds(ids);
                if (conversationId) {
                  supabase
                    .from("conversations")
                    .update({ selected_data_source_ids: ids } as any)
                    .eq("id", conversationId)
                    .then();
                }
              }}
              focusTrigger={focusTrigger}
            />
          )}
        </div>

        {/* Dashboard Panel */}
        {DASHBOARD_ON_CHAT && dashboardOpen && (
          <div className="hidden w-1/2 shrink-0 border-l border-border md:block">
            <DashboardPanel
              dashboard={dashboard}
              loading={dashboardLoading}
              onClose={() => {
                setDashboardOpen(false);
                setDashboard(null);
                setActiveDashboardId(null);
                updateUrlParams(conversationId, null);
              }}
              onDelete={activeDashboardId ? () => deleteDashboard(activeDashboardId) : undefined}
              snapshotId={activeDashboardId}
              onSaveFilters={activeDashboardId ? handleSaveFilters : undefined}
              onOpenInDashboardTab={activeDashboardId ? () => handleOpenInBuilder(activeDashboardId) : undefined}
              onDownloadPdf={activeDashboardId ? () => handleDownloadPdf(activeDashboardId, dashboard?.title || "Dashboard") : undefined}
            />
          </div>
        )}
      </div>

      {/* Mobile floating chat button – shown when input is collapsed */}
      {hasActiveChat && !mobileInputOpen && (
        <button
          onClick={() => { setMobileInputOpen(true); setFocusTrigger((p) => p + 1); }}
          className="md:hidden fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg active:scale-95 transition-transform"
          aria-label="Open chat input"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}

      {/* DashboardSubscribeDialog */}
      {DASHBOARD_ON_CHAT && subscribeSnapshotId && (
        <DashboardSubscribeDialog
          open={!!subscribeSnapshotId}
          onOpenChange={(open) => !open && setSubscribeSnapshotId(null)}
          dashboardSnapshotId={subscribeSnapshotId}
          dashboardTitle={subscribeSnapshotTitle}
        />
      )}

      {/* Per-message LLM debug dialog */}
      <ConversationDebugDialog
        messageId={debugMessageId}
        open={!!debugMessageId}
        onOpenChange={(open) => !open && setDebugMessageId(null)}
      />

      {/* How it works popup */}
      <HowItWorksDialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />

      {/* Research Templates */}
      <TemplateBrowserDialog
        open={researchTemplateBrowserOpen}
        onClose={() => setResearchTemplateBrowserOpen(false)}
        templateType="research"
        title="Choose a Research Template"
        description="Browse pre-built research analysis templates"
        onSelectTemplate={(t) => {
          setResearchTemplateBrowserOpen(false);
          setSelectedResearchTemplate(t);
          setResearchTemplatePromptOpen(true);
        }}
      />

      <TemplatePromptDialog
        open={researchTemplatePromptOpen}
        onClose={() => { setResearchTemplatePromptOpen(false); setSelectedResearchTemplate(null); }}
        template={selectedResearchTemplate}
        generateLabel="Start Research"
        placeholder="Describe the analysis you want to perform..."
        onGenerate={(prompt) => {
          setResearchTemplatePromptOpen(false);
          setSelectedResearchTemplate(null);
          sendMessage(prompt);
        }}
      />
    </div>
    </div>
  );
};

export default Chat;
